import { getOrganizationStats, readScopesEnabled } from "@/api";
import {
    Composer,
    ThreadFeaturedAgents,
} from "@/components/assistant-ui/thread";
import { FrontendPageContent } from "@/components/frontend/FrontendPageContent";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/auth/useAuth";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { cn } from "@/lib/utils";
import { useMountEffect } from "@react-hookz/web";
import { useSuspenseQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Suspense } from "react";
import { MetricsTile } from "./components/MetricsTile";
import { RecentConversations } from "./components/RecentConversations";
import { RecentDocuments } from "./components/RecentDocuments";
import { RecentScopes } from "./components/RecentScopes";

// Time-based salutation; the name is rendered separately so it can carry the
// greeting's one copper accent.
const getSalutation = (): string => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "Good morning";
    if (hour >= 12 && hour < 17) return "Good afternoon";
    if (hour >= 17 && hour < 21) return "Good evening";
    return "Late night";
};

// Salutation + date. `centered` = top of the chat-on hero (one axis with the
// composer); left-aligned = chat-off dashboard header above the metrics.
const Greeting = ({
    firstName,
    centered = false,
    className,
}: {
    firstName: string;
    centered?: boolean;
    className?: string;
}) => (
    <div
        className={cn(
            "animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none",
            centered && "text-center",
            className
        )}
    >
        <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
            <span className="text-foreground">{getSalutation()}, </span>
            <span className="text-copper">{firstName}</span>
        </h1>
        <p className="text-muted-foreground mt-1.5 text-sm">
            {format(new Date(), "EEEE, MMMM d")}
        </p>
    </div>
);

/**
 * Thin composition root: wires the greeting, the feature-flagged zone, and the
 * three recent-work widgets. Each widget owns its own data + loading state, so
 * the page never blocks on one fetch. The org-stats query lives in the gated
 * chat-off `MetricsSection` (its own Suspense), so chat-on neither issues nor
 * waits on a stats call it doesn't render.
 */
const OrganizationHome = () => {
    const { settings } = useOrganization();
    const { user } = useAuth();
    const { clearBreadcrumbs } = useBreadcrumb();

    useDocumentTitle("Home");

    useMountEffect(() => {
        clearBreadcrumbs();
    });

    if (!user) return null;

    const aiChatEnabled = settings.ai_chat_enabled;
    const scopesEnabled = readScopesEnabled(settings);

    return (
        <FrontendPageContent className="w-full overflow-y-auto p-4">
            <div className="mx-auto w-full max-w-6xl">
                {aiChatEnabled ? (
                    <section className="mb-14 flex flex-col items-center pt-4 sm:pt-10">
                        <Greeting firstName={user.first_name} centered />
                        <div
                            className="animate-in fade-in-0 slide-in-from-bottom-2 relative mt-6 w-full max-w-3xl motion-reduce:animate-none"
                            style={{
                                animationDelay: "80ms",
                                animationFillMode: "both",
                            }}
                        >
                            <div
                                style={{
                                    ["--composer-radius" as string]: "24px",
                                    ["--composer-padding" as string]: "10px",
                                }}
                            >
                                <ThreadFeaturedAgents />
                                <Composer />
                            </div>
                        </div>
                    </section>
                ) : (
                    <section className="mb-14 flex flex-col items-center pt-4 sm:pt-10">
                        <Greeting firstName={user.first_name} centered />
                        {scopesEnabled && (
                            <Suspense fallback={<MetricsSkeleton />}>
                                <MetricsSection />
                            </Suspense>
                        )}
                    </section>
                )}

                <div className="flex flex-col gap-8">
                    <div
                        className={cn(
                            "grid grid-cols-1 gap-5",
                            aiChatEnabled && "lg:grid-cols-2"
                        )}
                    >
                        {aiChatEnabled && <RecentConversations />}
                        <RecentDocuments />
                    </div>

                    {scopesEnabled && <RecentScopes />}
                </div>
            </div>
        </FrontendPageContent>
    );
};

/**
 * Chat-off metrics. Suspends on its own org-stats fetch behind a local
 * `<Suspense>` so the rest of the page (and the recent-work widgets) render
 * independently rather than blocking on this call.
 */
const MetricsSection = () => {
    const { organization } = useOrganization();
    const { data } = useSuspenseQuery({
        queryKey: ["frontend-organization-home", organization.id],
        queryFn: () => getOrganizationStats(organization.id),
    });

    return (
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 mt-8 flex flex-col items-center motion-reduce:animate-none">
            <h2 className="text-muted-foreground mb-3 text-sm font-medium">
                In the past 30 days, your team's processed
            </h2>
            <div className="grid grid-cols-3 gap-3 sm:max-w-lg">
                <MetricsTile label="scopes" number={data.scopes} />
                <MetricsTile label="pages" number={data.pages} />
                <MetricsTile label="clauses" number={data.clauses} />
            </div>
        </div>
    );
};

const MetricsSkeleton = () => {
    return (
        <div className="mt-8 flex flex-col items-center">
            <Skeleton className="mb-3 h-4 w-64" />
            <div className="grid grid-cols-3 gap-3 sm:max-w-lg">
                {Array.from({ length: 3 }, (_, i) => (
                    <Skeleton key={i} className="h-[5.25rem] rounded-xl" />
                ))}
            </div>
        </div>
    );
};

export default OrganizationHome;
